import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import type { ShippingPolicyResponse, CalculateShippingResponse } from "@models/shipping";
import {
    shippingPolicyAtom,
    isFetchingShippingPolicyAtom,
} from "@/store/shippingAtoms";

const SHIPPING_POLICY_ENDPOINT = "/shipping/policy";
const SHIPPING_CALCULATE_ENDPOINT = "/shipping/calculate";

export const useShippingApi = () => {
    const { callApi } = useApi();
    const setShippingPolicy = useSetAtom(shippingPolicyAtom);
    const setIsFetching = useSetAtom(isFetchingShippingPolicyAtom);

    // Anonymous endpoint — no bearer token, but useApi still attaches the
    // Functions key (x-functions-key), hence skipAuth rather than a raw fetch.
    const getPolicy = useCallback(async (): Promise<ShippingPolicyResponse> => {
        setIsFetching(true);

        try {
            const policy = await callApi<ShippingPolicyResponse>(
                SHIPPING_POLICY_ENDPOINT,
                { skipAuth: true },
            );

            setShippingPolicy(policy);

            return policy;
        } finally {
            setIsFetching(false);
        }
    }, [callApi, setShippingPolicy, setIsFetching]);

    const calculateShipping = useCallback(
        async (
            locationId: string,
            subtotal: number,
        ): Promise<CalculateShippingResponse> => {
            const params = new URLSearchParams({
                locationId,
                subtotal: subtotal.toString(),
            });
            return callApi<CalculateShippingResponse>(
                `${SHIPPING_CALCULATE_ENDPOINT}?${params.toString()}`,
            );
        },
        [callApi],
    );

    return {
        getPolicy,
        calculateShipping,
    };
};
